import classNames from 'classnames';
import type { BlockEmbed } from 'quill/blots/block';
import type { Op, Range } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import * as styles from './CodeEmbedEditor.module.scss';
import CodeEmbedEditorToolbar from './CodeEmbedEditorToolbar';
import Prism, { getGrammar } from './Prism';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import alertIcon from '../../icons/non-prefilled/16x16/alert.svg';
import addTestId from '../../utils/addTestId';
import CodeEmbedToken from '../formats/namespaces/code/CodeEmbedToken';
import QuillComponent from '../QuillComponent';
import QuillContext, {
  useQuill,
  useQuillReadOnly,
  useQuillState,
} from '../QuillContext';
import UserSelectContainer from '../utils/UserSelectContainer';

function tokenStreamToDelta(
  tokens: (string | Prism.Token)[],
  currentValue: string[] = [],
): Delta {
  let delta = new Delta();
  tokens.forEach((token) => {
    if (typeof token === 'string') {
      delta.retain(token.length);
      return;
    }
    const { type, content, length } = token;
    const newValue = [...currentValue, type];
    if (content) {
      const nestedDelta = tokenStreamToDelta(
        Array.isArray(content) ? content : [content],
        newValue,
      );
      delta = delta.concat(
        new Delta().compose(
          new Delta()
            .retain(length, { [CodeEmbedToken.keyName]: newValue })
            .compose(nestedDelta),
        ),
      );
    } else {
      delta.retain(length, { [CodeEmbedToken.keyName]: newValue });
    }
  });
  return delta;
}

const CodeLineNumbers = () => {
  const [lineCount, setLineCount] = useState(0);

  const { quill } = useQuill();

  useEffect(() => {
    if (!quill) return undefined;
    setLineCount(quill.scroll.children.length);
    const listener = () => {
      setLineCount(quill.scroll.children.length);
    };

    quill.on(Quill.events.TEXT_CHANGE, listener);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, listener);
    };
  }, [quill]);

  return lineCount > 0 ? (
    <div className={styles.lineNumbers}>
      {Array.from({ length: lineCount }).map((_, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={styles.lineNumber}
          onClick={() => {
            const line = quill?.scroll.children.at(i);
            if (line) {
              const offset = line.offset(line.scroll);
              if (offset >= 0) {
                quill?.setSelection(
                  { index: offset, length: 0 },
                  Quill.sources.USER,
                );
              }
            }
          }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  ) : null;
};

interface CodeEmbedEditorProps {
  blot: BlockEmbed;
  initialContent: Op[];
  language: string | null;
  syntaxError: Error | null;
  quillRef: MutableRefObject<Quill | null>;
  onContentChange: (content: Delta) => void;
  onSelectionChange: (range: Range | null, yChanged?: boolean) => void;
}

const CodeEmbedEditor = ({
  blot,
  initialContent,
  language,
  syntaxError,
  quillRef,
  onContentChange,
  onSelectionChange,
}: CodeEmbedEditorProps) => {
  const { activeBlot } = useQuillState();
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const contentRef = useRef(initialContent);

  const isSelected = activeBlot === blot;
  useEffect(() => {
    if (!readOnly && isSelected && quillRef.current?.hasFocus() === false) {
      quillRef.current.setSelection(0, 0, Quill.sources.SILENT);
    }
  }, [readOnly, isSelected, quillRef]);

  const grammarRef = useRef<Prism.Grammar | undefined>(getGrammar(language));
  useEffect(() => {
    grammarRef.current = getGrammar(language);
  }, [language]);

  const highlight = useCallback(() => {
    const childQuill = quillRef.current;
    if (!childQuill) return;
    const content = childQuill.getContents();
    const contentWithoutCodeToken = content.compose(
      new Delta().retain(content.length(), {
        [CodeEmbedToken.keyName]: null,
      }),
    );

    let delta = content.diff(contentWithoutCodeToken);
    if (grammarRef.current) {
      const tokens = Prism.tokenize(
        content
          .map((op) => (typeof op.insert === 'string' ? op.insert : ''))
          .join(''),
        grammarRef.current,
      );
      delta = delta.compose(tokenStreamToDelta(tokens));
    }
    if (delta.length()) {
      childQuill.updateContents(delta, Quill.sources.SILENT);
    }
  }, [quillRef]);

  useEffect(() => {
    highlight();
  }, [language, highlight]);

  const debouncedHighlight = useDebouncedCallback(highlight, 200, {
    maxWait: 1000,
  });

  return (
    <div
      className={classNames(styles.container, {
        [styles.selected]: isSelected,
      })}
    >
      <QuillContext>
        {isSelected && <CodeEmbedEditorToolbar blot={blot} />}
        <UserSelectContainer as="pre" spellCheck={false}>
          {quill && (
            <QuillComponent
              noTitleLine
              disallowOrphanFormats={['diff', 'comment']}
              content={contentRef.current}
              containerClassName={styles.editorContainer}
              rootClassName={styles.editor}
              namespace="code"
              contentPlaceholder=""
              readOnly={readOnly}
              quillRef={quillRef}
              onMount={() => highlight()}
              moduleConfig={{
                prefixListener: true,
                history: { parentQuill: quill },
                keyboard: { blot, parentQuill: quill },
                clipboard: { blot },
              }}
              theme="codeEmbedEditorTheme"
              onTextChange={(
                change: Delta,
                _oldContent: Delta,
                source: string,
              ) => {
                if (source === Quill.sources.USER) {
                  onContentChange(change);
                }

                const isMounted = !!quillRef.current;
                if (
                  isMounted &&
                  change.ops.some((op) => op.insert || op.delete)
                ) {
                  debouncedHighlight();
                }
              }}
              onSelectionChange={onSelectionChange}
            />
          )}
        </UserSelectContainer>
        <CodeLineNumbers />
      </QuillContext>
      {syntaxError && (
        <div
          className={styles.syntaxErrorWrapper}
          {...addTestId('codeEmbedSyntaxError')}
        >
          <Tooltip
            trigger="clickOrHover"
            placement="bottom"
            content={syntaxError.message}
          >
            <div className={styles.syntaxError}>
              <SVGIcon size="small">{alertIcon}</SVGIcon>
              Syntax Error
            </div>
          </Tooltip>
        </div>
      )}
      <div
        className={classNames(styles.exitPrompt, {
          [styles.selected]: !readOnly && isSelected,
          [styles.hidden]: syntaxError,
        })}
      />
    </div>
  );
};

export default CodeEmbedEditor;
